import { Component, Input, DoCheck, inject } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { CoreHttpClient, selectorCoreInstanceInfo } from '@bepatient/core';
import { CameraSource, CameraResultType } from '@capacitor/camera';
import {
  ActionSheetController,
  AlertController,
  Platform
} from '@ionic/angular';

import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { CommonUtilsService } from 'libs/common/src/lib/services/common-utils.service';
import { Subject, Subscription } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { PhotoQuestionV3 } from '../../../classes/question-photo';
import { QuestionBaseV3ComponentDirective } from '../question-base.component';

@Component({
  selector: 'bepatient-input-photo-v3',
  templateUrl: './input-photo.component.html',
  styleUrls: ['./input-photo.component.scss'],
  standalone: false
})
export class InputPhotoComponentV3
  extends QuestionBaseV3ComponentDirective
  implements DoCheck
{
  private http = inject(CoreHttpClient);
  private utils = inject(CommonUtilsService);
  private platform = inject(Platform);
  private translate = inject(TranslateService);
  private actionSheetController = inject(ActionSheetController);
  private alertController = inject(AlertController);
  private store$ = inject<Store<any>>(Store);

  @Input() question: PhotoQuestionV3;
  @Input() readonly: Boolean;
  @Input() formGroup: UntypedFormGroup;
  @Input() color: string;

  contrast: string;
  isBlur = true;
  dirty: boolean;

  fileSizeError = false;
  hasError: boolean;
  subscription: Subscription;
  fieldErrors: Array<string>;

  private photoSourceSubject = new Subject<CameraSource>();
  private photoSource$ = this.photoSourceSubject.asObservable();
  private isActionSheetOpen = false;

  get isWebview(): boolean {
    return !(this.platform.is('android') || this.platform.is('ios'));
  }

  get value(): string {
    const href = this.formGroup.get(this.question.key).value;

    if (this.question.value !== href) {
      if (href === null) {
        this.question.value = href;
      } else if (href?.startsWith('http')) {
        this.question.value = href;
        this.http
          .downloadBlob(this.question.value, {
            withMobiCredentials: true
          })
          .subscribe(
            blob => this.createImageFromBlob(blob.body),
            () => (this.question.src = null)
          );
      } else {
        this.question.value = href;
        // For !webview
        if (!href?.includes('data:')) {
          this.question.value = 'data:image/jpeg;base64,' + href;
          this.question.src = 'data:image/jpeg;base64,' + href;
        } else if (
          this.question.allowedExtension &&
          this.question.allowedExtension.length > 0 &&
          href &&
          !this.question.allowedExtension.some(ext => href?.includes(ext))
        ) {
          // If invalid extension remove the base64 string that is being displayed in the photo field
          this.question.src = undefined;
        } else {
          this.question.src = href;
        }
      }
    }
    return href;
  }

  // APP-3125: Loading image file from Webview
  public loadImageFromWebView(event: Event): void {
    const fileList = (event.target as HTMLInputElement).files;
    this.fileSizeError = false;
    // checking file is selected or not
    if (fileList && fileList.length > 0) {
      this.createImageFromBlob(fileList[0]);
      this.formGroup.get(this.question.key).markAsDirty();
      // get base64Image & base64EncodedFile
      // this.createImageFromBlob(fileList[0]);
    }
  }

  public takePicture(event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }

    // Prevent multiple action sheets from being created
    if (this.isActionSheetOpen) {
      return;
    }

    // if (!this.isWebview) {
    this.photoSource$
      .pipe(
        take(1),
        switchMap(source =>
          this.utils.takePicture({
            source: source,
            resultType: CameraResultType.DataUrl
          })
        )
      )
      .subscribe(
        imageData => {
          if (imageData && imageData.dataUrl)
            this.setFormControlValue(imageData.dataUrl);
        },
        error => error
      );

    this.question.useLibrary
      ? this.createActionSheet()
      : this.setPhotoSource(CameraSource.Camera);
    // }
  }

  public deleteImg(): void {
    const instanceInfo = this.store$.pipe(
      select(selectorCoreInstanceInfo),
      take(1)
    );

    this.alertController
      .create({
        mode: 'ios',
        cssClass: 'bepatient-notification-alert',
        backdropDismiss: false,
        message: this.translate.instant('PHOTO.DELETE_MESSAGE'),
        buttons: [
          {
            text: this.translate.instant('PHOTO.BUTTON.NO'),
            cssClass: 'bepatient-alert-single-button',
            role: 'cancel'
          },
          {
            text: this.translate.instant('PHOTO.BUTTON.YES'),
            cssClass: 'bepatient-alert-last-button',
            handler: () => {
              this.formGroup.get(this.question.key).reset();
              this.formGroup.get(this.question.key).markAsDirty();
            },
            role: 'ok'
          }
        ]
      })
      .then(alert => {
        alert.present();
        if (instanceInfo && instanceInfo['appColor']) {
          alert.style.setProperty(
            '--bepatient-notification-alert-color-primary',
            instanceInfo['appColor']
          );
        }
      });
  }

  onBlur(): void {
    this.isBlur = true;
    this.formGroup.get(this.question.key).markAsDirty();
  }

  onFocus(): void {
    this.isBlur = false;
  }

  isEmpty(): boolean {
    return !this.value;
  }

  ngDoCheck(): void {
    this.dirty = this.formGroup.get(this.question.key).dirty;
  }

  onChange(): void {
    this.isBlur = false;
    this.dirty = this.formGroup.get(this.question.key).dirty;
  }

  private setPhotoSource(source: CameraSource): void {
    Boolean(source)
      ? this.photoSourceSubject.next(source)
      : this.photoSourceSubject.next(null);
  }

  private createActionSheet(): void {
    if (this.isActionSheetOpen) {
      return;
    }

    this.isActionSheetOpen = true;
    this.actionSheetController
      .create({
        mode: this.platform.is('ios') ? 'ios' : 'md',
        cssClass: 'bepatient-action-sheet',
        buttons: [
          {
            text: this.translate.instant('PHOTO.CHOOSE_PHOTO_LIBRARY'),
            icon: 'image',
            cssClass: 'bepatient-action-sheet-button',
            handler: () => {
              this.setPhotoSource(CameraSource.Photos);
              this.isActionSheetOpen = false;
            }
          },
          {
            text: this.translate.instant('PHOTO.CHOOSE_CAMERA'),
            icon: 'camera',
            cssClass: 'bepatient-action-sheet-button',
            handler: () => {
              this.setPhotoSource(CameraSource.Camera);
              this.isActionSheetOpen = false;
            }
          },
          {
            text: this.translate.instant('CANCEL'),
            icon: 'close',
            cssClass: 'bepatient-action-sheet-button',
            role: 'cancel',
            handler: () => {
              this.isActionSheetOpen = false;
            }
          }
        ]
      })
      .then(actionSheet => {
        actionSheet.present();
        // Reset flag when action sheet is dismissed (by backdrop click or swipe)
        actionSheet.onDidDismiss().then(() => {
          this.isActionSheetOpen = false;
        });
      });
  }

  private setFormControlValue(src: string): void {
    // Mark input as touched & dirty - setting formControl value
    this.formGroup.get(this.question.key).markAsDirty();
    this.formGroup.get(this.question.key).markAsTouched();

    // For !isWebview add base64 to DATA_URI
    if (!src.includes('data:image')) {
      src = 'data:image/jpeg;base64,' + src;
    }

    this.formGroup.get(this.question.key).setValue(src);
    this.isBlur = false;
    this.dirty = this.formGroup.get(this.question.key).dirty;
  }

  private createImageFromBlob(imageBlob: Blob): void {
    const reader = new FileReader();

    reader.onload = () => (this.question.src = reader.result);

    if (imageBlob) {
      reader.readAsDataURL(imageBlob);
    }
  }
}
